/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

// override to add the wrapper for the total section to follow the general styles

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartContext, withCart } from "@insite/client-framework/Components/CartContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Pluralize from "@monsma/XDL/Components/Common/Pluralize";
import React from "react";

type Props = WidgetProps & HasCartContext;

// xng-custom XStyle injection, controlled from extentions
export interface XStyles {
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    itemsCount?: TypographyPresentationProps;
}

export interface SavedOrderDetailsTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    // custom
    xSavedOrderTotal?: XStyles;
}

export const savedOrderDetailsTotalStyles: SavedOrderDetailsTotalStyles = {};

const styles = savedOrderDetailsTotalStyles;

const SavedOrderDetailsTotal = ({ cart }: Props) => {
    const styles = mergeToNew(savedOrderDetailsTotalStyles, savedOrderDetailsTotalStyles.xSavedOrderTotal);

    if (!cart) {
        return null;
    }

    return (
        <>
            <Typography {...styles.title}>{translate("Order Summary")}</Typography>
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.itemsCount}>
                    <Pluralize
                        total={cart?.cartLines?.length || 0}
                        pluralLabel={translate("Items")}
                        singularLabel={translate("Item")}
                    />
                </Typography>
                <CartTotalDisplay
                    cart={cart}
                    showTaxAndShipping={false}
                    extendedStyles={styles.cartTotal}
                    {...({
                        orderSubTotalDisplayOnCart: cart?.properties?.orderSubTotalDisplayOnCart,
                    } as any)}
                />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withCart(SavedOrderDetailsTotal),
    definition: {
        group: "Saved Order Details",
        allowedContexts: ["SavedOrderDetailsPage"],
        displayName: "Saved Order Total",
    },
};

export default widgetModule;
